import type { ToolId } from "mediatools";
import { env } from "@/env";
import type { MediaKind } from "./types";

/**
 * Routing/group metadata for every media tool — the single source used by the
 * `/tools` directory and the `/studio` operation picker. Plain data only (no
 * React, no `mediabunny`) so importing it stays cheap on any page. Display copy
 * lives under `mediaTools.catalog` in the message files.
 */
export interface ToolCatalogEntry {
  id: ToolId;
  slug: string;
  group: MediaKind;
  readonly experimental?: boolean;
}

export const experimentalMediaToolsEnabled = () =>
  env.NEXT_PUBLIC_ENABLE_EXPERIMENTAL_MEDIA_TOOLS === "1";

export const TOOL_CATALOG: ToolCatalogEntry[] = [
  // Audio
  {
    id: "vocal-remover",
    slug: "/vocal-remover",
    group: "audio",
    experimental: true,
  },
  {
    id: "speech-to-text",
    slug: "/speech-to-text",
    group: "audio",
    experimental: true,
  },
  {
    id: "optimize-audio",
    slug: "/optimize-audio",
    group: "audio",
  },
  {
    id: "extract-audio",
    slug: "/extract-audio",
    group: "audio",
  },
  {
    id: "convert-audio",
    slug: "/convert-audio",
    group: "audio",
  },
  {
    id: "trim-audio",
    slug: "/trim-audio",
    group: "audio",
  },
  {
    id: "merge-audio",
    slug: "/merge-audio",
    group: "audio",
  },
  {
    id: "normalize-audio",
    slug: "/normalize-audio",
    group: "audio",
  },
  // Video
  {
    id: "subtitle-editor",
    slug: "/subtitle-editor",
    group: "video",
  },
  {
    id: "trim-video",
    slug: "/trim-video",
    group: "video",
  },
  {
    id: "convert-video",
    slug: "/convert-video",
    group: "video",
  },
  {
    id: "compress-video",
    slug: "/compress-video",
    group: "video",
  },
  {
    id: "mute-video",
    slug: "/mute-video",
    group: "video",
  },
  {
    id: "rotate-video",
    slug: "/rotate-video",
    group: "video",
  },
  {
    id: "extract-frame",
    slug: "/extract-frame",
    group: "video",
  },
];

export const TOOL_CATALOG_BY_ID: Partial<Record<ToolId, ToolCatalogEntry>> = Object.fromEntries(
  TOOL_CATALOG.map((entry) => [entry.id, entry])
);

export function visibleToolCatalog(): ToolCatalogEntry[] {
  if (experimentalMediaToolsEnabled()) return TOOL_CATALOG;
  return TOOL_CATALOG.filter((entry) => !entry.experimental);
}

export function visibleToolCatalogById(): Partial<Record<ToolId, ToolCatalogEntry>> {
  return Object.fromEntries(visibleToolCatalog().map((entry) => [entry.id, entry]));
}
